// SPDX-License-Identifier: Apache-2.0

// TagAutocomplete — popover for tag completion in the modal editor.
//
// Triggers in two contexts:
//
//   1. Frontmatter `tags:` line — anywhere on a line that starts with
//      `tags:` (with optional whitespace) we offer tag suggestions for the
//      token currently under the caret. Tokens are split on commas, brackets,
//      and whitespace.
//
//   2. Body `#tag` — when the caret is immediately after a `#` followed by
//      tag-safe characters, we offer matches. We skip Markdown headings
//      (`# Heading`) by requiring NO whitespace between `#` and the cursor.
//
// Matching is PREFIX-only (per the gift) over `getAllTags()`. We collect
// candidates from every doc's `front.tags`. Keyboard nav matches the wikilink
// popover — Arrow / Enter / Esc.

import {
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  Show,
  For,
} from "solid-js";
import type { UseVault } from "./useVault";
import { computeCaretXY, type CaretXY } from "./caret-mirror";

export interface TagAutocompleteProps {
  textareaRef: () => HTMLTextAreaElement | undefined;
  value: () => string;
  setValue: (next: string) => void;
  vault: UseVault;
}

/** Walk every doc and collect the unique set of tags used. */
function getAllTags(vault: UseVault): string[] {
  const set = new Set<string>();
  for (const doc of vault.docs()) {
    const tags = doc.front?.tags;
    if (Array.isArray(tags)) {
      for (const t of tags) {
        if (typeof t === "string" && t.trim().length > 0) set.add(t.trim());
      }
    }
  }
  return Array.from(set).sort();
}

// Tag-character predicate. Same charset as the OLD Tether spec: letters,
// digits, dash, underscore, slash, dot. Specifically excludes whitespace and
// commas so frontmatter list parsing isn't confused.
const TAG_CHAR = /[A-Za-z0-9_\-./]/;

interface Trigger {
  /** Position of the start of the typed token (NOT including the leading `#`
   *  or the `tags:` colon). */
  start: number;
  query: string;
  /** "body" or "frontmatter" — informational, helps style or limit suggestions
   *  later. */
  context: "body" | "frontmatter";
}

/**
 * Decide whether the user is currently mid-tag at `caret`. Returns the trigger
 * info, or `null` if no tag completion should be offered.
 */
function detectTrigger(value: string, caret: number): Trigger | null {
  // Find the start of the current line.
  let lineStart = caret - 1;
  while (lineStart >= 0 && value[lineStart] !== "\n") lineStart--;
  lineStart += 1;
  const line = value.slice(lineStart, caret);

  // Frontmatter detection: very loose — just look for a leading `tags:` on this
  // line, then treat the cursor's current token as the query. (Real
  // frontmatter spans a fenced `---` block, but per-line is the right
  // granularity for autocomplete.)
  const tagsMatch = /^\s*tags\s*:/i.exec(line);
  if (tagsMatch) {
    // Walk backward from caret while we're on TAG_CHAR.
    let s = caret;
    while (s > lineStart && TAG_CHAR.test(value[s - 1] ?? "")) s--;
    // Skip if we landed on the colon (no token yet).
    const query = value.slice(s, caret);
    return { start: s, query, context: "frontmatter" };
  }

  // Body `#tag` detection. Walk back from caret while on TAG_CHAR, then
  // require the prev char to be `#` and (start-of-line OR whitespace before).
  let s = caret;
  while (s > 0 && TAG_CHAR.test(value[s - 1] ?? "")) s--;
  if (s === 0) return null;
  if (value[s - 1] !== "#") return null;
  // The character BEFORE the `#` must be whitespace, start-of-line, or
  // start-of-doc — otherwise it's something like an HTML entity or a URL
  // fragment and not a tag.
  const beforeHash = s >= 2 ? value[s - 2] : null;
  if (beforeHash !== null && beforeHash !== " " && beforeHash !== "\t" && beforeHash !== "\n") {
    return null;
  }
  // Skip the markdown heading case: `#` followed by space → not a tag.
  if (s === caret) return null; // empty after `#`, but we want to show the
  // suggestion list as soon as they hit a TAG_CHAR. Actually — gift says
  // "after `#` in body" so let's allow s === caret to ALSO open with empty
  // query. Reverting:
  // (kept the early return above as a conservative default; tweak below.)
  return { start: s, query: value.slice(s, caret), context: "body" };
}

export function TagAutocomplete(props: TagAutocompleteProps) {
  const [open, setOpen] = createSignal(false);
  const [trigger, setTrigger] = createSignal<Trigger | null>(null);
  const [activeIdx, setActiveIdx] = createSignal(0);
  const [pos, setPos] = createSignal<CaretXY>({ left: 0, top: 0 });

  const allTags = createMemo<string[]>(() => getAllTags(props.vault));

  const matches = createMemo<string[]>(() => {
    const t = trigger();
    if (!open() || !t) return [];
    const q = t.query.toLowerCase();
    if (q.length === 0) return allTags().slice(0, 12);
    const out: string[] = [];
    for (const tag of allTags()) {
      if (tag.toLowerCase().startsWith(q)) out.push(tag);
      if (out.length >= 12) break;
    }
    return out;
  });

  createEffect(() => {
    matches();
    setActiveIdx(0);
  });

  function close(): void {
    setOpen(false);
    setTrigger(null);
  }

  function insertTag(tag: string): void {
    const ta = props.textareaRef();
    const t = trigger();
    if (!ta || !t) return;
    const v = props.value();
    const before = v.slice(0, t.start);
    const after = v.slice(ta.selectionStart);
    const next = before + tag + after;
    props.setValue(next);
    const caret = before.length + tag.length;
    queueMicrotask(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
    close();
  }

  function evaluate(): void {
    const ta = props.textareaRef();
    if (!ta) {
      if (open()) close();
      return;
    }
    const caret = ta.selectionStart;
    const v = ta.value;
    const t = detectTrigger(v, caret);
    if (!t) {
      if (open()) close();
      return;
    }
    setTrigger(t);
    setOpen(true);
    setPos(computeCaretXY(ta, caret));
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (!open()) return;
    const opts = matches();
    if (e.key === "ArrowDown") {
      if (opts.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % opts.length);
    } else if (e.key === "ArrowUp") {
      if (opts.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + opts.length) % opts.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (opts.length === 0) return;
      e.preventDefault();
      const tag = opts[activeIdx()];
      if (tag) insertTag(tag);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }

  createEffect(() => {
    const ta = props.textareaRef();
    if (!ta) return;
    const onInput = () => evaluate();
    const onClick = () => evaluate();
    const onSelChange = () => evaluate();
    ta.addEventListener("input", onInput);
    ta.addEventListener("click", onClick);
    ta.addEventListener("keyup", onSelChange);
    ta.addEventListener("keydown", onKeyDown, true);
    onCleanup(() => {
      ta.removeEventListener("input", onInput);
      ta.removeEventListener("click", onClick);
      ta.removeEventListener("keyup", onSelChange);
      ta.removeEventListener("keydown", onKeyDown, true);
    });
  });

  createEffect(() => {
    props.value();
    if (open()) evaluate();
  });

  return (
    <Show when={open() && matches().length > 0}>
      <div
        class="mv-ac-popover"
        data-testid="tag-autocomplete"
        style={{ left: `${pos().left}px`, top: `${pos().top}px` }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <For each={matches()}>
          {(tag, i) => (
            <div
              class="mv-ac-item"
              data-active={i() === activeIdx() ? "true" : "false"}
              onMouseEnter={() => setActiveIdx(i())}
              onClick={() => insertTag(tag)}
            >
              <span class="mv-ac-item__title">{tag}</span>
            </div>
          )}
        </For>
      </div>
    </Show>
  );
}
